import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UnderaiterMark, AIRAMark, FullLogo } from '@/components/brand/Marks';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowRight, CheckCircle, Clock, Briefcase } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LoginPage() {
  const { login, completeMfa } = useAuth();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'waitlist' ? 'waitlist' : searchParams.get('mode') === 'apply' ? 'apply' : 'login';
  const [staffMode, setStaffMode] = useState(searchParams.get('staff') === '1');
  const oauthError = searchParams.get('error');
  const [mode, setMode] = useState(initialMode); // login | waitlist | waitlist_success | apply | apply_success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [applyMsg, setApplyMsg] = useState('');
  const [error, setError] = useState('');
  const [googleOnly, setGoogleOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setGoogleOnly(false);
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data?.mfa_required) setMfaChallenge(data.challenge_token);
    } catch (err) {
      console.error("API error:", err?.message);
      const detail = err.response?.data?.detail;
      if (detail === 'GOOGLE_ONLY_ACCOUNT') setGoogleOnly(true);
      else if (typeof detail === 'string') setError(detail);
      else setError('Invalid email or password.');
    }
    setSubmitting(false);
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await completeMfa(mfaChallenge, mfaCode);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid MFA code.');
      if (err.response?.status === 401 && (detail || '').includes('challenge')) {
        setMfaChallenge(null); setMfaCode('');
      }
    }
    setSubmitting(false);
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/waitlist`, { name, email, company, role });
      setMode('waitlist_success');
    } catch (err) {
      console.error("API error:", err?.message);
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/signup/apply`, { name, email, brokerage: company, phone, message: applyMsg });
      setApplyMsg(data.message || '');
      setMode('apply_success');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="w-full lg:w-[480px] flex flex-col justify-center px-8 lg:px-12 bg-white" style={{ borderRight: '1px solid #e2e8f0' }}>
        <div className="max-w-sm mx-auto w-full">
          <div className="mb-10">
            <FullLogo size="md" />
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Sign In
              </h2>
              <p className="text-sm mb-6" style={{ color: '#5C6B7A' }}>
                Access is invite-only. Sign in with the Google account approved for your organisation, or with the email and password you were given.
              </p>

              {oauthError && (
                <div className="flex items-start gap-2 text-sm p-3 mb-4 rounded-sm" style={{ background: 'rgba(226,75,74,0.12)', color: '#B03230', border: '1px solid rgba(226,75,74,0.3)' }} data-testid="oauth-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{oauthError}</span>
                </div>
              )}

              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button data-testid="google-signin-button"
                onClick={() => {
                  const redirectUrl = window.location.origin + '/today';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 rounded-sm font-semibold transition-all duration-150 hover:bg-slate-50 hover:shadow-sm"
                style={{ height: 48, border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a' }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Sign in with Google
              </button>

              {!staffMode && (
                <button type="button" data-testid="password-signin-toggle" onClick={() => setStaffMode(true)}
                  className="w-full mt-4 text-sm font-medium transition-colors duration-150 hover:underline"
                  style={{ color: '#1B4A9A' }}>
                  Have an email and password? Sign in here
                </button>
              )}

              {/* Email + password sign-in (team, broker and staff accounts) */}
              {staffMode && !mfaChallenge && (
                <>
                  <div className="flex items-center gap-3 mt-6">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs" style={{ color: '#5C6B7A' }}>email and password</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  <form onSubmit={handleLogin} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Email</Label>
                      <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Password</Label>
                      <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 text-sm p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.15)', color: '#E24B4A' }} data-testid="auth-error">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    {googleOnly && (
                      <div className="flex items-start gap-2 text-sm p-3 rounded-sm" style={{ background: 'rgba(0,180,216,0.08)', color: '#0E7490', border: '1px solid rgba(0,180,216,0.3)' }} data-testid="google-only-notice">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>This account uses Google sign-in. Use the <strong>Sign in with Google</strong> button above.</span>
                      </div>
                    )}

                    <Button data-testid="login-submit-button" type="submit" disabled={submitting}
                      className="w-full rounded-sm font-semibold text-white transition-all duration-150"
                      style={{ background: '#1B4A9A', height: 44 }}>
                      {submitting ? 'Please wait...' : 'Sign In'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </>
              )}

              {/* MFA step — 6-digit authenticator code or recovery code */}
              {staffMode && mfaChallenge && (
                <form onSubmit={handleMfa} className="space-y-4 mt-6" data-testid="login-mfa-step">
                  <div className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Two-factor authentication</div>
                  <p className="text-xs" style={{ color: '#5C6B7A' }}>Enter the 6-digit code from your authenticator app, or a recovery code.</p>
                  <Input data-testid="login-mfa-code-input" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
                    inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={32}
                    className="mt-1 bg-white border-slate-300 text-slate-900 font-mono tracking-widest focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required />
                  {error && (
                    <div className="flex items-center gap-2 text-sm p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.15)', color: '#E24B4A' }} data-testid="mfa-error">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  <Button data-testid="login-mfa-submit-button" type="submit" disabled={submitting}
                    className="w-full rounded-sm font-semibold text-white transition-all duration-150"
                    style={{ background: '#1B4A9A', height: 44 }}>
                    {submitting ? 'Verifying...' : 'Verify'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <button type="button" onClick={() => { setMfaChallenge(null); setMfaCode(''); setError(''); }}
                    className="text-xs hover:underline" style={{ color: '#5C6B7A' }} data-testid="mfa-back-btn">← Back to sign-in</button>
                </form>
              )}

              {/* Waitlist + broker apply links */}
              <div className="mt-6 pt-5 border-t border-slate-200 space-y-2.5">
                <button onClick={() => { setMode('waitlist'); setError(''); }}
                  className="flex items-center gap-2 text-sm hover:underline transition-colors" style={{ color: '#FF9933' }}>
                  <Clock className="w-4 h-4" />
                  Request a demo — Join the waitlist
                </button>
                <button onClick={() => { setMode('apply'); setError(''); }} data-testid="broker-apply-link"
                  className="flex items-center gap-2 text-sm hover:underline transition-colors" style={{ color: '#1A4C7C' }}>
                  <Briefcase className="w-4 h-4" />
                  Broker? Apply to join the panel
                </button>
              </div>
            </>
          )}

          {/* Waitlist Form */}
          {mode === 'waitlist' && (
            <>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Join the Waitlist
              </h2>
              <p className="text-sm mb-6" style={{ color: '#5C6B7A' }}>
                Request access to a full product demo. We'll reach out to schedule.
              </p>
              <form onSubmit={handleWaitlist} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Full Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required
                    data-testid="waitlist-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required
                    data-testid="waitlist-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Company</Label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)}
                    placeholder="Your company name"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required
                    data-testid="waitlist-company" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Role</Label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white border border-slate-300 text-slate-900 rounded-sm text-sm focus:ring-2 focus:ring-[#1B4A9A]"
                    data-testid="waitlist-role">
                    <option value="" style={{ color: '#0D1B2A' }}>Select your role</option>
                    <option value="CUO / Underwriting Manager" style={{ color: '#0D1B2A' }}>CUO / Underwriting Manager</option>
                    <option value="MGA Principal" style={{ color: '#0D1B2A' }}>MGA Principal</option>
                    <option value="Broker" style={{ color: '#0D1B2A' }}>Broker</option>
                    <option value="Claims Manager" style={{ color: '#0D1B2A' }}>Claims Manager</option>
                    <option value="Compliance Officer" style={{ color: '#0D1B2A' }}>Compliance Officer</option>
                    <option value="Technology / Operations" style={{ color: '#0D1B2A' }}>Technology / Operations</option>
                    <option value="Investor" style={{ color: '#0D1B2A' }}>Investor</option>
                    <option value="Other" style={{ color: '#0D1B2A' }}>Other</option>
                  </select>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.15)', color: '#E24B4A' }}>
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button type="submit" disabled={submitting}
                  className="w-full rounded-sm font-semibold text-white transition-all duration-150"
                  style={{ background: '#FF9933', height: 44 }}
                  data-testid="waitlist-submit">
                  {submitting ? 'Submitting...' : 'Join the Waitlist'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <button onClick={() => { setMode('login'); setError(''); }}
                className="mt-5 text-sm hover:underline transition-colors" style={{ color: '#5C6B7A' }}>
                Already have access? Sign in
              </button>
            </>
          )}

          {/* Waitlist Success */}
          {mode === 'waitlist_success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: 'rgba(29,158,117,0.15)' }}>
                <CheckCircle className="w-8 h-8" style={{ color: '#1D9E75' }} />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                You're on the list
              </h2>
              <p className="text-sm" style={{ color: '#5C6B7A' }}>
                We'll reach out to schedule your product demo. In the meantime, check out our 3-minute platform walkthrough.
              </p>
              <a href="/demo" className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 rounded-sm text-sm font-semibold text-white" style={{ background: '#00B4D8' }}>
                Watch Demo <ArrowRight className="w-4 h-4" />
              </a>
              <div className="mt-4">
                <button onClick={() => { setMode('login'); setError(''); }}
                  className="text-sm hover:underline transition-colors" style={{ color: '#5C6B7A' }}>
                  Back to Sign In
                </button>
              </div>
            </div>
          )}
          {/* Broker Application Form */}
          {mode === 'apply' && (
            <>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Apply to Join the Panel
              </h2>
              <p className="text-sm mb-6" style={{ color: '#5C6B7A' }}>
                Tell us about you and your brokerage. Once approved, you'll get an activation link by email — set your own password and quote in minutes.
              </p>
              <form onSubmit={handleApply} className="space-y-4" data-testid="broker-apply-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Your Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required data-testid="apply-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@brokerage.com.au"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required data-testid="apply-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Brokerage</Label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Your brokerage / company name"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" required data-testid="apply-brokerage" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Phone (optional)</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="04xx xxx xxx"
                    className="mt-1 bg-white border-slate-300 text-slate-900 focus:ring-2 focus:ring-[#1B4A9A] rounded-sm" data-testid="apply-phone" />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.15)', color: '#E24B4A' }} data-testid="apply-error">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button type="submit" disabled={submitting}
                  className="w-full rounded-sm font-semibold text-white transition-all duration-150"
                  style={{ background: '#1B4A9A', height: 44 }} data-testid="apply-submit">
                  {submitting ? 'Submitting...' : 'Submit Application'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
              <button onClick={() => { setMode('login'); setError(''); }}
                className="mt-5 text-sm hover:underline transition-colors" style={{ color: '#5C6B7A' }}>
                Already have access? Sign in
              </button>
            </>
          )}

          {/* Broker Application Success */}
          {mode === 'apply_success' && (
            <div className="text-center py-8" data-testid="apply-success">
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: 'rgba(29,158,117,0.15)' }}>
                <CheckCircle className="w-8 h-8" style={{ color: '#1D9E75' }} />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Application received
              </h2>
              <p className="text-sm" style={{ color: '#5C6B7A' }}>
                {applyMsg || "Your MGA will review your application and email you a secure activation link — usually within a business day."}
              </p>
              <div className="mt-6">
                <button onClick={() => { setMode('login'); setError(''); }}
                  className="text-sm hover:underline transition-colors" style={{ color: '#5C6B7A' }}>
                  Back to Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right - Hero */}
      <div className="hidden lg:flex flex-1 relative items-center justify-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0B1E3D 0%, #1B4A9A 100%)' }}>
        <img src="https://images.unsplash.com/photo-1766866771433-c3042a3ce7a3?w=1200&q=80"
          alt="Modern architecture" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 max-w-lg text-center px-8">
          <AIRAMark size="text-6xl" dark />
          <p className="mt-6 text-2xl font-semibold text-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="login-tagline">
            Run your entire MGA on one platform.
          </p>
          <p className="mt-3 text-sm text-white/60">
            Underwriting, claims, renewals, compliance and finance — one source of truth.
          </p>
          <p className="mt-5 text-[11px] uppercase tracking-[0.35em] text-white/45" style={{ fontFamily: "'Courier New', monospace" }}>
            Evolutionary Insurance Intelligence.
          </p>
        </div>
      </div>
    </div>
  );
}
